import React, { Component, createRef } from "react";

// CSS
import "./Courses.scss";

// Helper
import { checkCookie } from "../../Helper/Cookies";
import { apiFetch, getCurrentUser, uploadCourseMedia, UploadCancelledError } from "../../Helper/Api";
import { hasModule } from "../../Helper/category";

// Components
import Header from "../../components/Header/Header";
import ReactDOM from "react-dom";
import { SelectEmpty } from "../../components/UI/Controls";
import { Alert } from "../../components/UI/Feedback";
import { Confirm } from "../../components/UI/Overlays";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { FileUpload } from "primereact/fileupload";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";

// Properties
const properties = require("../../properties.json");

const ITEM_TYPES = [
  { label: "فيديو", value: "VIDEO" },
  { label: "صوت", value: "AUDIO" },
  { label: "صورة", value: "IMAGE" },
  { label: "مذكرة / مستند", value: "DOCUMENT" },
  { label: "نموذج 3D (Rhino)", value: "MODEL_3D" },
  { label: "مقال / وصفة (نص وصورة)", value: "ARTICLE" },
];

const EMPTY_ITEM_FORM = { title: "", description: "", itemType: "VIDEO", contentUrl: "", fileName: "", thumb: "", articleText: "", isFreePreview: false, downloadAllowed: false };

// Used by the bulk multi-file uploader to auto-pick an item type per file instead of making the
// teacher set one dropdown per file -- matches CourseItemType on the backend.
const EXTENSION_ITEM_TYPE = {
  mp4: "VIDEO", mov: "VIDEO", avi: "VIDEO", webm: "VIDEO", mkv: "VIDEO", m4v: "VIDEO",
  mp3: "AUDIO", wav: "AUDIO", m4a: "AUDIO", aac: "AUDIO", ogg: "AUDIO", wma: "AUDIO",
  jpg: "IMAGE", jpeg: "IMAGE", png: "IMAGE", gif: "IMAGE", webp: "IMAGE", bmp: "IMAGE",
  "3dm": "MODEL_3D",
};

function detectItemType(fileName) {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  return EXTENSION_ITEM_TYPE[ext] || "DOCUMENT";
}

function stripExtension(fileName) {
  const idx = fileName.lastIndexOf(".");
  return idx > 0 ? fileName.slice(0, idx) : fileName;
}

class CourseBuilder extends Component {
  constructor(props) {
    super(props);
    this.toast = createRef(null);
    // Unique per-file key for bulkUploads, so two files sharing a name in the same drop (or two
    // overlapping batches) get their own progress row instead of clobbering each other's state.
    this.bulkUploadSeq = 0;
  }

  state = {
    id: this.props.courseId
      ? this.props.courseId
      : this.props.match && this.props.match.params
      ? this.props.match.params.id
      : null,

    course: {
      title: "",
      description: "",
      image: "",
      listImage: "",
      branchId: getCurrentUser()?.branchId,
      materialId: null,
      periodId: null,
      planId: null,
      instructorId: null,
      kind: "PROGRAM",
      level: null,
      sortOrder: 0,
      showOnHome: false,
    },
    sections: [],
    plans: [], // membership plans (an account without school periods opens a program with a plan)
    instructors: [],
    teamMode: null,

    materials: [],
    periods: [],
    allCourses: [], // this teacher's other courses, for the "move item" section/course picker

    newSectionTitle: "",
    itemForms: {}, // sectionId -> form state
    bulkUploads: {}, // sectionId -> { fileName -> progress percent } for the multi-file uploader
    editingSectionId: null,
    editingSectionTitle: "",
    movingItem: null, // { item, sectionId, targetCourseId, targetSectionId } while the move UI is open
    uploadingThumb: false,
    uploadingThumbProgress: 0,
    uploadingListImage: false,
    uploadingListImageProgress: 0,
    saving: false,

    // the panel header's slot for this form's save, and whether each menu
    // is still fetching its options
    headerSlot: null,

    // which sections have the one-by-one form open; dropping files is the
    // main way in, so it starts closed
    manualFor: {},

    // what the teacher is being asked to confirm; the browser's own box is
    // never used anywhere in this product
    ask: null,

    // the course own data is on its way: the form and the sections are
    // covered until it lands, instead of showing empty fields that fill
    // themselves a second later
    loadingCourse: !!(
      this.props.courseId ||
      (this.props.match && this.props.match.params && this.props.match.params.id)
    ),

    loadingMaterials: true,
    loadingPeriods: false,
  };

  // a coach account has no school periods: a program is opened by a membership plan instead
  planMode = () => !hasModule("SCHOOL_STRUCTURE") && hasModule("MEMBERSHIPS");

  loadGymLists = () => {
    if (!this.planMode()) return;
    apiFetch("/plans").then((plans) => this.setState({ plans })).catch(() => {});
    if (hasModule("INSTRUCTORS")) {
      apiFetch("/teachers/me").then((me) => {
        this.setState({ teamMode: me.teamMode || "SOLO" });
        if (me.teamMode === "TEAM") apiFetch("/instructors").then((instructors) => this.setState({ instructors })).catch(() => {});
      }).catch(() => {});
    }
  };

  componentDidMount() {
    document.title = properties.site_name + " | " + (this.state.id ? "تعديل الدورة" : "دورة جديدة");
    this.loadGymLists();

    if (!checkCookie("name")) {
      window.localStorage.removeItem("currentTeacher");
      window.location.href = `/`;
    }

    // in a panel the save belongs on the header row
    if (this.props.inPanel) {
      const slot = document.querySelector(".ui-panel-slot");
      if (slot) this.setState({ headerSlot: slot });
    }

    this.loadMaterials();
    if (this.state.id) {
      this.loadCourse();
      this.loadAllCourses();
      this.processingTimer = setInterval(this.pollProcessing, 15000);
    }
  }

  componentWillUnmount() {
    clearInterval(this.processingTimer);
  }

  /** While a video is queued or being encoded, pick up its new status without touching anything else on the page
   *  (a full reload would overwrite what the teacher is typing). */
  pollProcessing = () => {
    const waiting = (this.state.sections || []).some((s) =>
      (s.items || []).some((i) => i.processingStatus === "PENDING" || i.processingStatus === "PROCESSING")
    );
    if (!waiting) return;
    apiFetch(`/courses/${this.state.id}`)
      .then((data) => {
        const fresh = {};
        (data.sections || []).forEach((s) => (s.items || []).forEach((i) => (fresh[i.id] = i)));
        this.setState((st) => ({
          sections: st.sections.map((s) => ({
            ...s,
            items: (s.items || []).map((i) => {
              const f = fresh[i.id];
              return f ? { ...i, processingStatus: f.processingStatus, processingError: f.processingError, renditions: f.renditions } : i;
            }),
          })),
        }));
      })
      .catch(() => {});
  };

  /** Every course this teacher owns, each with its own sections -- lets an item be moved to a
   *  different section without leaving this page, including a section under a different course. */
  loadAllCourses = () => {
    apiFetch("/courses")
      .then((allCourses) => this.setState({ allCourses }))
      .catch((error) => console.error("Error:", error));
  };

  loadMaterials = () => {
    const user = getCurrentUser();
    if (!user || !hasModule("SCHOOL_STRUCTURE")) return;
    this.setState({ loadingMaterials: true });
    apiFetch("/lookup/materials", { query: { branchId: user.branchId, teacherUserId: user.userId } })
      .then((materials) => this.setState({ materials, loadingMaterials: false }))
      .catch((error) => {
        this.setState({ loadingMaterials: false });
        console.error("Error:", error);
      });
  };

  loadPeriods = (materialId) => {
    if (!materialId) {
      this.setState({ periods: [] });
      return;
    }
    this.setState({ loadingPeriods: true });
    apiFetch("/periods/by-material", { query: { materialId } })
      .then((periods) => this.setState({ periods, loadingPeriods: false }))
      .catch((error) => {
        this.setState({ loadingPeriods: false });
        console.error("Error:", error);
      });
  };

  loadCourse = () => {
    apiFetch(`/courses/${this.state.id}`)
      .then((data) => {
        this.setState({
          course: {
            title: data.title,
            description: data.description,
            image: data.image,
            listImage: data.listImage,
            branchId: data.branchId,
            materialId: data.materialId,
            periodId: data.periodId,
            planId: data.planId || null,
            instructorId: data.instructorId || null,
            kind: data.kind || "COURSE",
            level: data.level || null,
            sortOrder: data.sortOrder,
            showOnHome: !!data.showOnHome,
          },
          sections: data.sections || [],
          loadingCourse: false,
        });
        this.loadPeriods(data.materialId);
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ loadingCourse: false });
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: "تعذر تحميل الدورة" });
      });
  };

  // what opens the course for a student: a school period, or (coach account) a membership plan
  hasAccess = (course) => (this.planMode() ? !!course.planId : !!course.periodId);

  saveCourse = (e) => {
    e.preventDefault();
    const { course, id } = this.state;
    if (!course.title || !course.branchId || !this.hasAccess(course)) {
      this.toast.current.show({ severity: "error", summary: "تنبيه", detail: this.planMode() ? "من فضلك أدخل إسم البرنامج وإختر خطة العضوية" : "من فضلك أدخل إسم الدورة وإختر الفترة" });
      return;
    }
    this.setState({ saving: true });
    const request = apiFetch(id ? `/courses/${id}` : "/courses", { method: id ? "PUT" : "POST", body: course });
    request
      .then((saved) => {
        this.setState({ saving: false });
        this.toast.current.show({ severity: "success", summary: "تم بنجاح", detail: "تم حفظ الدورة بنجاح" });

        // The panel holds the course's own fields, and saving them finishes
        // that task: whoever opened it closes it and refetches. The content
        // is a separate action, opened from the table in its own modal.
        if (this.props.inPanel) {
          if (this.props.onSaved) this.props.onSaved(saved);
          return;
        }

        // On the standalone page nothing changes: a new course still sends
        // the browser to its edit URL, since sections need a saved course.
        if (!id) {
          window.location.href = `/courses/edit/${saved.id}`;
        } else {
          this.loadCourse();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setState({ saving: false });
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  /** Refreshes both this course's own detail AND the full teacher's-courses list (the latter
   *  feeds the "move item" section/course picker, which needs to stay in sync with whatever just
   *  changed -- a newly added section, a renamed one, etc). */
  refreshCourse = () => {
    this.loadCourse();
    this.loadAllCourses();
  };

  // stage / subject folder for this course's files in storage
  uploadContext = () => ({ courseId: this.state.id, materialId: this.state.course && this.state.course.materialId });

  uploadCourseThumb = async (event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.setState({ uploadingThumb: true, uploadingThumbProgress: 0, uploadingThumbXhr: null });
    try {
      const url = await uploadCourseMedia(
        file,
        (pct) => this.setState({ uploadingThumbProgress: pct }),
        (xhr) => this.setState({ uploadingThumbXhr: xhr }),
        this.uploadContext()
      );
      this.setState({ course: { ...this.state.course, image: url }, uploadingThumb: false, uploadingThumbXhr: null });
      this.toast.current.show({ severity: "info", summary: "تم بنجاح", detail: "تم رفع صورة الدورة" });
    } catch (error) {
      this.setState({ uploadingThumb: false, uploadingThumbXhr: null });
      if (error instanceof UploadCancelledError) return;
      console.error("Error:", error);
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الصورة" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  cancelThumbUpload = () => {
    this.state.uploadingThumbXhr && this.state.uploadingThumbXhr.abort();
  };

  // Separate from image/uploadCourseThumb above -- image is the home page + course detail cover,
  // this is what shows on the /courses listing card (see Courses.js on the public frontend).
  uploadCourseListImage = async (event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.setState({ uploadingListImage: true, uploadingListImageProgress: 0, uploadingListImageXhr: null });
    try {
      const url = await uploadCourseMedia(
        file,
        (pct) => this.setState({ uploadingListImageProgress: pct }),
        (xhr) => this.setState({ uploadingListImageXhr: xhr }),
        this.uploadContext()
      );
      this.setState({ course: { ...this.state.course, listImage: url }, uploadingListImage: false, uploadingListImageXhr: null });
      this.toast.current.show({ severity: "info", summary: "تم بنجاح", detail: "تم رفع صورة صفحة الدورات" });
    } catch (error) {
      this.setState({ uploadingListImage: false, uploadingListImageXhr: null });
      if (error instanceof UploadCancelledError) return;
      console.error("Error:", error);
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الصورة" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  cancelListImageUpload = () => {
    this.state.uploadingListImageXhr && this.state.uploadingListImageXhr.abort();
  };

  addSection = () => {
    if (!this.state.newSectionTitle) return;
    apiFetch(`/courses/${this.state.id}/sections`, {
      method: "POST",
      body: { title: this.state.newSectionTitle, sortOrder: this.state.sections.length },
    })
      .then(() => {
        this.setState({ newSectionTitle: "" });
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  /** Asks in the product own card; the browser box is never used here. */
  confirmThen = (name, note, onYes) => this.setState({ ask: { name, note, onYes } });

  deleteSection = (sectionId) => {
    const section = (this.state.sections || []).find((s) => s.id === sectionId);
    this.confirmThen(
      section ? section.title : "هذا القسم",
      "القسم هيتشال بكل الدروس اللي جوّاه، ومش هينفع ترجع عنه.",
      () => this.reallyDeleteSection(sectionId)
    );
  };

  reallyDeleteSection = (sectionId) => {
    apiFetch(`/courses/${this.state.id}/sections/${sectionId}`, { method: "DELETE" })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  // Renumbers every section to its new list position (0..n-1) rather than just swapping the two
  // moved sections' existing sortOrder values -- some courses (migrated data in particular) have
  // sections that already share a sortOrder, so swapping two equal values was a silent no-op: the
  // PUTs succeeded but nothing visibly moved. Rewriting the whole list's order on every move also
  // self-heals any such duplicates going forward.
  moveSection = (section, direction) => {
    const { sections } = this.state;
    const index = sections.findIndex((s) => s.id === section.id);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= sections.length) return;
    const reordered = [...sections];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    Promise.all(
      reordered.map((s, i) =>
        apiFetch(`/courses/${this.state.id}/sections/${s.id}`, { method: "PUT", body: { title: s.title, sortOrder: i } })
      )
    )
      .then(() => this.refreshCourse())
      .catch((error) => console.error("Error:", error));
  };

  startEditSection = (section) => {
    this.setState({ editingSectionId: section.id, editingSectionTitle: section.title });
  };

  cancelEditSection = () => {
    this.setState({ editingSectionId: null, editingSectionTitle: "" });
  };

  saveEditSectionTitle = (section) => {
    const title = this.state.editingSectionTitle.trim();
    if (!title) return;
    apiFetch(`/courses/${this.state.id}/sections/${section.id}`, { method: "PUT", body: { title, sortOrder: section.sortOrder } })
      .then(() => {
        this.setState({ editingSectionId: null, editingSectionTitle: "" });
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  updateItemForm = (sectionId, patch) => {
    this.setState({
      itemForms: {
        ...this.state.itemForms,
        [sectionId]: { ...(this.state.itemForms[sectionId] || EMPTY_ITEM_FORM), ...patch },
      },
    });
  };

  uploadItemFile = async (sectionId, event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.updateItemForm(sectionId, { uploading: true, uploadProgress: 0, uploadXhr: null });
    try {
      const url = await uploadCourseMedia(
        file,
        (pct) => this.updateItemForm(sectionId, { uploadProgress: pct }),
        (xhr) => this.updateItemForm(sectionId, { uploadXhr: xhr }),
        this.uploadContext()
      );
      this.updateItemForm(sectionId, { contentUrl: url, fileName: file.name, uploading: false, uploadXhr: null });
      this.toast.current.show({ severity: "info", summary: "تم بنجاح", detail: "تم رفع الملف" });
    } catch (error) {
      this.updateItemForm(sectionId, { uploading: false, uploadXhr: null });
      if (error instanceof UploadCancelledError) return;
      console.error("Error:", error);
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الملف" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  cancelItemUpload = (sectionId) => {
    const form = this.state.itemForms[sectionId];
    form && form.uploadXhr && form.uploadXhr.abort();
  };

  removeUploadedItemFile = (sectionId) => {
    this.updateItemForm(sectionId, { contentUrl: "", fileName: "" });
  };

  // the picture of a written page (recipe, guide)
  uploadItemThumb = async (sectionId, event) => {
    const file = event.files && event.files[0];
    if (!file) return;
    this.updateItemForm(sectionId, { uploadingThumb: true });
    try {
      const url = await uploadCourseMedia(file, undefined, undefined, this.uploadContext());
      this.updateItemForm(sectionId, { thumb: url, uploadingThumb: false });
    } catch (error) {
      this.updateItemForm(sectionId, { uploadingThumb: false });
      if (error instanceof UploadCancelledError) return;
      this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "فشل رفع الصورة" });
    } finally {
      event.options && event.options.clear && event.options.clear();
    }
  };

  // ---- a written page: edit its text (the title and the flags have their own controls) ----
  startEditArticle = (section, item) => this.setState({ editingArticle: { section, item, title: item.title, text: item.articleText || "", saving: false } });

  saveArticle = () => {
    const { section, item, title, text } = this.state.editingArticle;
    if (!title.trim() || !text.trim()) {
      this.toast.current.show({ severity: "error", summary: "تنبيه", detail: "اكتب العنوان والنص" });
      return;
    }
    this.setState({ editingArticle: { ...this.state.editingArticle, saving: true } });
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, {
      method: "PUT",
      body: {
        id: item.id,
        title: title.trim(),
        description: item.description,
        itemType: item.itemType,
        contentUrl: item.contentUrl,
        thumb: item.thumb,
        articleText: text,
        isFreePreview: !!item.isFreePreview,
        downloadAllowed: !!item.downloadAllowed,
        sortOrder: item.sortOrder,
      },
    })
      .then(() => {
        this.setState({ editingArticle: null });
        this.refreshCourse();
      })
      .catch((error) => {
        this.setState({ editingArticle: { ...this.state.editingArticle, saving: false } });
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  addItem = (section) => {
    const form = this.state.itemForms[section.id] || EMPTY_ITEM_FORM;
    const article = form.itemType === "ARTICLE";
    if (!form.title || (article ? !form.articleText || !form.articleText.trim() : !form.contentUrl)) {
      this.toast.current.show({ severity: "error", summary: "تنبيه", detail: article ? "من فضلك أدخل العنوان والنص" : "من فضلك أدخل العنوان وارفع الملف" });
      return;
    }
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items`, {
      method: "POST",
      body: {
        title: form.title,
        description: form.description,
        itemType: form.itemType,
        contentUrl: form.contentUrl,
        thumb: form.thumb,
        articleText: article ? form.articleText : undefined,
        isFreePreview: !!form.isFreePreview,
        downloadAllowed: !!form.downloadAllowed,
        sortOrder: (section.items || []).length,
      },
    })
      .then(() => {
        this.updateItemForm(section.id, EMPTY_ITEM_FORM);
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  /** Per-section queue (one promise chain per sectionId) that the "create item" call after each
   *  bulk-upload file goes through -- see uploadBulkFiles below for why. Lives outside state since
   *  it's pure plumbing, not something a render should ever read. */
  bulkFinalizeQueues = {};

  enqueueBulkFinalize = (sectionId, task) => {
    const previous = this.bulkFinalizeQueues[sectionId] || Promise.resolve();
    // Chained on both branches so one file's failed finalize never wedges the rest of the queue.
    const next = previous.then(task, task);
    this.bulkFinalizeQueues[sectionId] = next;
    return next;
  };

  updateBulkUpload = (sectionId, uploadId, patch) => {
    this.setState((s) => {
      const sectionUploads = s.bulkUploads[sectionId] || {};
      const current = sectionUploads[uploadId];
      if (patch === null) {
        const remaining = { ...sectionUploads };
        delete remaining[uploadId];
        return { bulkUploads: { ...s.bulkUploads, [sectionId]: remaining } };
      }
      return {
        bulkUploads: {
          ...s.bulkUploads,
          [sectionId]: { ...sectionUploads, [uploadId]: { ...(current || { progress: 0, xhr: null }), ...patch } },
        },
      };
    });
  };

  /** Bulk add: every dropped file becomes its own item, titled after its filename and typed by
   *  its extension (see detectItemType) -- for when a teacher has a folder of lesson files ready
   *  to go rather than adding them one at a time with a title/type per file.
   *
   *  The file transfers themselves run in parallel (each file gets its own progress row, updating
   *  independently, instead of the teacher watching one bar at a time while the rest sit at 0%).
   *  But the "create item" call that finalizes each upload is serialized per section through
   *  enqueueBulkFinalize -- manasa-service shouldn't see two of those hit the same course at once
   *  (each one reads the section's current item count to pick its sortOrder, so two racing writes
   *  could both land on the same value). sortOrder itself is precomputed once, up front, from this
   *  batch's starting item count + the file's index -- reading it fresh per-file from state would
   *  reintroduce the same race, since refreshCourse()'s re-fetch after each finalize won't have
   *  landed yet by the time the next one in the queue runs. */
  uploadBulkFiles = (section, event) => {
    const files = event.files || [];
    if (!files.length) return;
    const sectionId = section.id;
    const baseSortOrder = (section.items || []).length;

    files.forEach((file, index) => {
      const uploadId = ++this.bulkUploadSeq;
      this.updateBulkUpload(sectionId, uploadId, { progress: 0, xhr: null, fileName: file.name });

      uploadCourseMedia(
        file,
        (pct) => this.updateBulkUpload(sectionId, uploadId, { progress: pct }),
        (xhr) => this.updateBulkUpload(sectionId, uploadId, { xhr }),
        this.uploadContext()
      )
        .then((url) =>
          this.enqueueBulkFinalize(sectionId, () =>
            apiFetch(`/courses/${this.state.id}/sections/${sectionId}/items`, {
              method: "POST",
              body: {
                title: stripExtension(file.name),
                itemType: detectItemType(file.name),
                contentUrl: url,
                isFreePreview: false,
                downloadAllowed: false,
                sortOrder: baseSortOrder + index,
              },
            }).then(() => this.refreshCourse())
          )
        )
        .catch((error) => {
          if (error instanceof UploadCancelledError) return;
          console.error("Error:", error);
          this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: `فشل رفع ${file.name}: ${error.message || "خطأ في الإتصال"}` });
        })
        .finally(() => this.updateBulkUpload(sectionId, uploadId, null));
    });

    event.options && event.options.clear && event.options.clear();
  };

  cancelBulkUpload = (sectionId, uploadId) => {
    const info = (this.state.bulkUploads[sectionId] || {})[uploadId];
    info && info.xhr && info.xhr.abort();
  };

  deleteItem = (section, item) => {
    this.confirmThen(item.title, "الدرس ده هيتشال من القسم نهائياً.", () =>
      this.reallyDeleteItem(section, item)
    );
  };

  reallyDeleteItem = (section, item) => {
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, { method: "DELETE" })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  reprocessItem = (section, item) => {
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}/reprocess`, { method: "POST" })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  // Where a video is in the encoder (qualities for the player are made after upload): shown next to the title.
  renderProcessing = (section, item) => {
    if (item.itemType !== "VIDEO" || !item.processingStatus) return null;
    const s = item.processingStatus;
    if (s === "READY") {
      return <span className="p-tag p-tag-success" title="جودات متاحة للمشاهدة">{item.renditions ? `جاهز · ${item.renditions.replace(/,/g, "p / ")}p` : "جاهز"}</span>;
    }
    if (s === "FAILED") {
      return (
        <span>
          <span className="p-tag p-tag-danger" title={item.processingError || ""}>فشلت المعالجة</span>
          <Button label="إعادة المحاولة" icon="pi pi-refresh" className="p-button-text p-button-sm" onClick={() => this.reprocessItem(section, item)} />
        </span>
      );
    }
    return <span className="p-tag p-tag-warning" title="الطلاب سيشاهدونه بعد انتهاء التجهيز">{s === "PENDING" ? "في الانتظار" : "جاري تجهيز الفيديو"}</span>;
  };

  // Same full-list renumbering as moveSection above, and for the same reason -- swapping just the
  // two moved items' existing sortOrder values was a silent no-op whenever they happened to share
  // one already.
  moveItem = (section, item, direction) => {
    const items = section.items || [];
    const index = items.findIndex((i) => i.id === item.id);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    const bodyFor = (it, sortOrder) => ({
      title: it.title,
      description: it.description,
      itemType: it.itemType,
      contentUrl: it.contentUrl,
      thumb: it.thumb,
      isFreePreview: it.isFreePreview,
      downloadAllowed: !!it.downloadAllowed,
      sortOrder,
    });
    Promise.all(
      reordered.map((it, i) =>
        apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${it.id}`, { method: "PUT", body: bodyFor(it, i) })
      )
    )
      .then(() => this.refreshCourse())
      .catch((error) => console.error("Error:", error));
  };

  /** Renames an item in place. Same endpoint and same body the two toggles
   *  below already send -- only the title differs. */
  renameItem = (section, item, title) => {
    const next = String(title || "").trim();
    if (!next || next === item.title) return;

    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, {
      method: "PUT",
      body: {
        title: next,
        description: item.description,
        itemType: item.itemType,
        contentUrl: item.contentUrl,
        thumb: item.thumb,
        isFreePreview: !!item.isFreePreview,
        downloadAllowed: !!item.downloadAllowed,
        sortOrder: item.sortOrder,
      },
    })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  toggleItemFreePreview = (section, item, checked) => {
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, {
      method: "PUT",
      body: {
        title: item.title,
        description: item.description,
        itemType: item.itemType,
        contentUrl: item.contentUrl,
        thumb: item.thumb,
        isFreePreview: checked,
        downloadAllowed: !!item.downloadAllowed,
        sortOrder: item.sortOrder,
      },
    })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  toggleItemDownload = (section, item, checked) => {
    apiFetch(`/courses/${this.state.id}/sections/${section.id}/items/${item.id}`, {
      method: "PUT",
      body: {
        title: item.title,
        description: item.description,
        itemType: item.itemType,
        contentUrl: item.contentUrl,
        thumb: item.thumb,
        isFreePreview: item.isFreePreview,
        downloadAllowed: checked,
        sortOrder: item.sortOrder,
      },
    })
      .then(() => this.refreshCourse())
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  startMoveItem = (section, item) => {
    this.setState({ movingItem: { item, sectionId: section.id, targetCourseId: this.state.id, targetSectionId: section.id } });
  };

  cancelMoveItem = () => {
    this.setState({ movingItem: null });
  };

  confirmMoveItem = () => {
    const { movingItem, id } = this.state;
    if (!movingItem || !movingItem.targetSectionId) return;
    if (movingItem.targetCourseId === Number(id) && movingItem.targetSectionId === movingItem.sectionId) {
      // No actual change -- just close the picker.
      this.setState({ movingItem: null });
      return;
    }
    apiFetch(`/courses/${id}/items/${movingItem.item.id}/move`, {
      method: "PUT",
      body: { targetCourseId: movingItem.targetCourseId, targetSectionId: movingItem.targetSectionId },
    })
      .then(() => {
        this.setState({ movingItem: null });
        this.toast.current.show({ severity: "success", summary: "تم بنجاح", detail: "تم نقل العنصر" });
        this.refreshCourse();
      })
      .catch((error) => {
        console.error("Error:", error);
        this.toast.current.show({ severity: "error", summary: "لم تتم العملية", detail: error.message || "خطأ في الإتصال" });
      });
  };

  render() {
    // "details" -- the course's own fields, shown in the panel from the
    // table; "content" -- its sections and lessons, shown in the modal from
    // the table's own action; neither -- the standalone page, both halves.
    const view = this.props.view || "all";

    const { course, sections, materials, periods, id, allCourses, movingItem, editingSectionId, editingSectionTitle, uploadingThumbProgress, bulkUploads } = this.state;

    const materialOptions = materials.map((m) => ({ label: m.name, value: m.id }));
    const periodOptions = periods.map((p) => ({ label: p.name, value: p.id }));
    const courseOptions = allCourses.map((c) => ({ label: c.title, value: c.id }));
    const moveTargetCourse = movingItem ? allCourses.find((c) => c.id === movingItem.targetCourseId) : null;
    const moveSectionOptions = moveTargetCourse ? (moveTargetCourse.sections || []).map((s) => ({ label: s.title, value: s.id })) : [];

    return (
      <div className={this.props.inPanel ? "add-lesson-page in-panel" : "add-lesson-page"}>
        {this.props.inPanel ? null : (
          <Header pageName={id ? "تعديل الدورة" : "دورة جديدة"} />
        )}

        <Dialog
        header="تعديل المقال"
        visible={!!this.state.editingArticle}
        style={{ width: "min(640px, 94vw)" }}
        onHide={() => this.setState({ editingArticle: null })}
        footer={
          <Button
            label={this.state.editingArticle && this.state.editingArticle.saving ? "جاري الحفظ..." : "حفظ"}
            icon="pi pi-check"
            disabled={!!(this.state.editingArticle && this.state.editingArticle.saving)}
            onClick={this.saveArticle}
          />
        }
      >
        {this.state.editingArticle ? (
          <div className="add-item-form">
            <InputText
              value={this.state.editingArticle.title}
              placeholder="العنوان"
              onChange={(e) => this.setState({ editingArticle: { ...this.state.editingArticle, title: e.target.value } })}
            />
            <InputTextarea
              rows={12}
              autoResize
              maxLength={20000}
              value={this.state.editingArticle.text}
              onChange={(e) => this.setState({ editingArticle: { ...this.state.editingArticle, text: e.target.value } })}
            />
          </div>
        ) : null}
      </Dialog>
      <Toast ref={this.toast}></Toast>

        {/* the layout is already here; the veil just covers it until the
            course own sections and fields have arrived */}
        {this.state.loadingCourse ? (
          <div className="ui-form-wait" role="status">
            <span className="spin" aria-hidden="true"></span>
            <b>
              {view === "content"
                ? "جاري تحميل أقسام الدورة…"
                : "جاري تحميل بيانات الدورة…"}
            </b>
          </div>
        ) : null}

        {/* every "are you sure" in this product is this card, never the
            browser own box */}
        <Confirm
          visible={!!this.state.ask}
          onHide={() => this.setState({ ask: null })}
          name={this.state.ask ? this.state.ask.name : ""}
          note={this.state.ask ? this.state.ask.note : ""}
          onConfirm={() => {
            const ask = this.state.ask;
            this.setState({ ask: null });
            if (ask) ask.onYes();
          }}
        />

        <div className="content">
          <div className="container">
            {/* `view` splits this component in two without splitting the
                component: "details" is the course's own fields (the panel
                from the table), "content" is its sections and lessons (the
                modal from the table's own action). The standalone page
                passes nothing and still renders both, as it always did. */}
            {!id && view === "all" ? (
              <Alert kind="info" title="احفظ الدورة الأول.">
                الأقسام والمحتوى بيتضافوا بعد ما الدورة تتحفظ لأول مرة.
              </Alert>
            ) : null}

            <Card
              className={view === "content" ? "ui-step-hidden" : undefined}
              title={this.props.inPanel ? null : (id ? "بيانات الدورة" : "إدخال دورة جديدة")}
            >
              <form id="course-form" onSubmit={this.saveCourse} action="#" method="POST">
                <div className="p-grid p-fluid">
                  <div className="p-col-12 p-md-6">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <InputText
                          id="course-title"
                          required
                          value={course.title}
                          onChange={(e) => this.setState({ course: { ...course, title: e.target.value } })}
                        />
                        <label htmlFor="course-title">إسم الدورة</label>
                      </span>
                    </div>
                  </div>
                  {this.planMode() ? (
                    <>
                      <div className="p-col-12 p-md-6">
                        <div className="p-inputgroup">
                          <span className="p-float-label">
                            <Dropdown
                              value={course.planId}
                              options={this.state.plans.map((p) => ({ label: p.name, value: p.id }))}
                              onChange={(e) => this.setState({ course: { ...course, planId: e.value } })}
                              placeholder="اختر"
                              emptyMessage={<SelectEmpty text="اعمل خطة عضوية الأول" />}
                            />
                            <label>خطة العضوية</label>
                          </span>
                        </div>
                      </div>
                      <div className="p-col-12 p-md-6">
                        <div className="p-inputgroup">
                          <span className="p-float-label">
                            <Dropdown
                              value={course.level}
                              showClear
                              options={[{ label: "مبتدئ", value: "BEGINNER" }, { label: "متوسط", value: "INTERMEDIATE" }, { label: "متقدم", value: "ADVANCED" }]}
                              onChange={(e) => this.setState({ course: { ...course, level: e.value } })}
                              placeholder="اختر"
                            />
                            <label>المستوى</label>
                          </span>
                        </div>
                      </div>
                      {this.state.teamMode === "TEAM" ? (
                        <div className="p-col-12 p-md-6">
                          <div className="p-inputgroup">
                            <span className="p-float-label">
                              <Dropdown
                                value={course.instructorId}
                                showClear
                                options={this.state.instructors.filter((i) => i.enabled).map((i) => ({ label: i.name, value: i.id }))}
                                onChange={(e) => this.setState({ course: { ...course, instructorId: e.value } })}
                                placeholder="اختر"
                              />
                              <label>المدرب</label>
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                  <div className="p-col-12 p-md-6">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <Dropdown
                          value={course.materialId}
                          options={materialOptions}
                          showClear
                          onChange={(e) => {
                            this.setState({ course: { ...course, materialId: e.value, periodId: null } });
                            this.loadPeriods(e.value);
                          }}
                          placeholder="اختر"
                          emptyMessage={<SelectEmpty loading={this.state.loadingMaterials} />}
                        />
                        <label>المادة</label>
                      </span>
                    </div>
                  </div>
                  <div className="p-col-12 p-md-6">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <Dropdown
                          value={course.periodId}
                          options={periodOptions}
                          required
                          onChange={(e) => this.setState({ course: { ...course, periodId: e.value } })}
                          placeholder="اختر"
                          emptyMessage={<SelectEmpty loading={this.state.loadingPeriods} />}
                          disabled={!course.materialId}
                        />
                        <label>
                          الفترة
                          {!course.materialId ? (
                            <small className="ui-fieldhint">اختر المادة الأول</small>
                          ) : null}
                        </label>
                      </span>
                    </div>
                  </div>
                    </>
                  )}
                  <div className="p-col-12 p-md-12">
                    <div className="p-inputgroup">
                      <span className="p-float-label">
                        <InputTextarea
                          rows={4}
                          value={course.description}
                          onChange={(e) => this.setState({ course: { ...course, description: e.target.value } })}
                        />
                        <label>وصف الدورة</label>
                      </span>
                    </div>
                  </div>
                  <div className="p-col-12 p-md-12 course-show-on-home-field">
                    <Checkbox
                      inputId="showOnHome"
                      checked={!!course.showOnHome}
                      onChange={(e) => this.setState({ course: { ...course, showOnHome: e.checked } })}
                    />
                    <label htmlFor="showOnHome">إظهار هذه الدورة في الصفحة الرئيسية</label>
                  </div>
                  <div className="p-col-12 p-md-12">
                    <FileUpload
                      name="courseThumb"
                      customUpload
                      headerTemplate={(o) => (
                        <div className="ui-sec-head is-sub">
                          <span className="t">
                            صورة الغلاف
                            <small>بتظهر في الصفحة الرئيسية وصفحة تفاصيل الدورة</small>
                          </span>
                          <span className="act">{o.chooseButton}</span>
                        </div>
                      )}
                      uploadHandler={this.uploadCourseThumb}
                      auto
                      disabled={this.state.uploadingThumb}
                      multiple={false}
                      accept="image/*"
                      maxFileSize={20000000}
                      chooseLabel={this.state.uploadingThumb ? "جاري الرفع..." : "إختيار صورة الغلاف"}
                      uploadLabel="رفع الصورة"
                      cancelLabel="إلغاء"
                      emptyTemplate={<p className="p-m-0">إسحب صورة الغلاف هنا ليبدأ التحميل</p>}
                    />
                    {this.state.uploadingThumb ? (
                      <div className="upload-progress-row">
                        <ProgressBar value={uploadingThumbProgress} style={{ flex: 1, height: "10px" }} />
                        <span>{uploadingThumbProgress}%</span>
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger p-button-sm"
                          title="إلغاء الرفع"
                          onClick={this.cancelThumbUpload}
                        />
                      </div>
                    ) : null}
                    {course.image ? (
                      <div className="uploaded-file-row">
                        <img className="course-thumb-preview" src={course.image} alt="course" />
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger"
                          onClick={() => this.setState({ course: { ...course, image: "" } })}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="p-col-12 p-md-12">
                    <FileUpload
                      name="courseListImage"
                      customUpload
                      headerTemplate={(o) => (
                        <div className="ui-sec-head is-sub">
                          <span className="t">
                            صورة صفحة الدورات
                            <small>بتظهر في بطاقة الدورة داخل صفحة الدورات</small>
                          </span>
                          <span className="act">{o.chooseButton}</span>
                        </div>
                      )}
                      uploadHandler={this.uploadCourseListImage}
                      auto
                      disabled={this.state.uploadingListImage}
                      multiple={false}
                      accept="image/*"
                      maxFileSize={20000000}
                      chooseLabel={this.state.uploadingListImage ? "جاري الرفع..." : "إختيار صورة صفحة الدورات"}
                      uploadLabel="رفع الصورة"
                      cancelLabel="إلغاء"
                      emptyTemplate={<p className="p-m-0">إسحب صورة صفحة الدورات هنا ليبدأ التحميل</p>}
                    />
                    {this.state.uploadingListImage ? (
                      <div className="upload-progress-row">
                        <ProgressBar value={this.state.uploadingListImageProgress} style={{ flex: 1, height: "10px" }} />
                        <span>{this.state.uploadingListImageProgress}%</span>
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger p-button-sm"
                          title="إلغاء الرفع"
                          onClick={this.cancelListImageUpload}
                        />
                      </div>
                    ) : null}
                    {course.listImage ? (
                      <div className="uploaded-file-row">
                        <img className="course-thumb-preview" src={course.listImage} alt="course list" />
                        <Button
                          icon="pi pi-times"
                          className="p-button-rounded p-button-text p-button-danger"
                          onClick={() => this.setState({ course: { ...course, listImage: "" } })}
                        />
                      </div>
                    ) : null}
                  </div>
                  {/* in a panel the save rides the header row; on the page it
                      keeps its own bar here. Same element, same state. */}
                  {this.state.headerSlot && view !== "content" ? (
                    ReactDOM.createPortal(
                      <Button
                        form="course-form"
                        className={`add-button ${this.state.saving ? "loading" : ""}`}
                        label={id ? "حفظ التعديلات" : "حفظ الدورة"}
                        disabled={!course.title || !this.hasAccess(course) || this.state.saving}
                      />,
                      this.state.headerSlot
                    )
                  ) : (
                    <div className="p-col-12 p-md-12">
                      <Button
                        className={`add-button ${this.state.saving ? "loading" : ""}`}
                        label={id ? "حفظ التعديلات" : "حفظ ومتابعة إضافة المحتوى"}
                        disabled={!course.title || !this.hasAccess(course) || this.state.saving}
                      />
                    </div>
                  )}
                </div>
              </form>
            </Card>

            {id ? (
              <Card
                className={view === "details" ? "ui-step-hidden" : undefined}
                title={this.props.inPanel ? null : "محتوى الدورة (الأقسام والدروس)"}
              >
                {sections.map((section, sIndex) => {
                  const form = this.state.itemForms[section.id] || EMPTY_ITEM_FORM;
                  return (
                    <div className="course-section" key={section.id}>
                      <div className="course-section-header">
                        {editingSectionId === section.id ? (
                          <div className="section-title-edit">
                            <InputText
                              value={editingSectionTitle}
                              autoFocus
                              onChange={(e) => this.setState({ editingSectionTitle: e.target.value })}
                              onKeyDown={(e) => e.key === "Enter" && this.saveEditSectionTitle(section)}
                            />
                            <Button icon="pi pi-check" className="p-button-rounded p-button-text p-button-success" onClick={() => this.saveEditSectionTitle(section)} />
                            <Button icon="pi pi-times" className="p-button-rounded p-button-text" onClick={this.cancelEditSection} />
                          </div>
                        ) : (
                          <h4>
                            {section.title}
                            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm" onClick={() => this.startEditSection(section)} />
                          </h4>
                        )}
                        <div>
                          <Button icon="pi pi-arrow-up" className="p-button-rounded p-button-text" disabled={sIndex === 0} onClick={() => this.moveSection(section, -1)} />
                          <Button icon="pi pi-arrow-down" className="p-button-rounded p-button-text" disabled={sIndex === sections.length - 1} onClick={() => this.moveSection(section, 1)} />
                          <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.deleteSection(section.id)} />
                        </div>
                      </div>

                      {(section.items || []).map((item, iIndex) => (
                        <div key={item.id}>
                          <div className="course-item-row">
                            <div className="course-item-info">
                              <i className={`pi ${item.itemType === "VIDEO" ? "pi-video" : item.itemType === "AUDIO" ? "pi-volume-up" : item.itemType === "IMAGE" ? "pi-image" : item.itemType === "MODEL_3D" ? "pi-box" : item.itemType === "ARTICLE" ? "pi-book" : "pi-file"}`} />
                              <span>{item.title}</span>
                              {this.renderProcessing(section, item)}
                              <label className="item-free-preview-toggle">
                                <Checkbox checked={!!item.isFreePreview} onChange={(e) => this.toggleItemFreePreview(section, item, e.checked)} />
                                {" "}معاينة مجانية
                              </label>
                              <label className="item-free-preview-toggle">
                                <Checkbox checked={!!item.downloadAllowed} onChange={(e) => this.toggleItemDownload(section, item, e.checked)} />
                                {" "}السماح بالتحميل
                              </label>
                            </div>
                            <div>
                              {item.itemType === "ARTICLE" ? (
                                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" title="تعديل النص" onClick={() => this.startEditArticle(section, item)} />
                              ) : null}
                              <Button icon="pi pi-arrow-up" className="p-button-rounded p-button-text" disabled={iIndex === 0} onClick={() => this.moveItem(section, item, -1)} />
                              <Button icon="pi pi-arrow-down" className="p-button-rounded p-button-text" disabled={iIndex === (section.items || []).length - 1} onClick={() => this.moveItem(section, item, 1)} />
                              <Button icon="pi pi-directions" className="p-button-rounded p-button-text" title="نقل إلى قسم/دورة أخرى" onClick={() => this.startMoveItem(section, item)} />
                              <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.deleteItem(section, item)} />
                            </div>
                          </div>
                          {movingItem && movingItem.item.id === item.id ? (
                            <div className="move-item-form">
                              <Dropdown
                                value={movingItem.targetCourseId}
                                options={courseOptions}
                                onChange={(e) => {
                                  const targetCourse = allCourses.find((c) => c.id === e.value);
                                  const firstSectionId = targetCourse && targetCourse.sections && targetCourse.sections.length ? targetCourse.sections[0].id : null;
                                  this.setState({ movingItem: { ...movingItem, targetCourseId: e.value, targetSectionId: firstSectionId } });
                                }}
                                placeholder="الدورة"
                              />
                              <Dropdown
                                value={movingItem.targetSectionId}
                                options={moveSectionOptions}
                                onChange={(e) => this.setState({ movingItem: { ...movingItem, targetSectionId: e.value } })}
                                placeholder="القسم"
                              />
                              <Button label="نقل" icon="pi pi-check" onClick={this.confirmMoveItem} />
                              <Button label="إلغاء" className="p-button-text" onClick={this.cancelMoveItem} />
                            </div>
                          ) : null}
                        </div>
                      ))}

                      <div className="add-item-form">
                        <InputText
                          placeholder="عنوان العنصر"
                          value={form.title}
                          onChange={(e) => this.updateItemForm(section.id, { title: e.target.value })}
                        />
                        <Dropdown
                          value={form.itemType}
                          options={ITEM_TYPES}
                          onChange={(e) => this.updateItemForm(section.id, { itemType: e.value, contentUrl: "" })}
                          placeholder="نوع العنصر"
                        />
                        {form.itemType === "ARTICLE" ? (
                          <>
                            <InputTextarea
                              rows={8}
                              autoResize
                              maxLength={20000}
                              placeholder="النص (المكونات، الخطوات، النصائح...). كل سطر جديد بيظهر في سطر جديد."
                              value={form.articleText || ""}
                              onChange={(e) => this.updateItemForm(section.id, { articleText: e.target.value })}
                            />
                            <FileUpload
                              name={`article-thumb-${section.id}`}
                              customUpload
                              uploadHandler={(e) => this.uploadItemThumb(section.id, e)}
                              auto
                              disabled={!!form.uploadingThumb}
                              multiple={false}
                              accept="image/*"
                              maxFileSize={10485760}
                              chooseLabel={form.uploadingThumb ? "جاري رفع الصورة..." : form.thumb ? "استبدال الصورة" : "رفع صورة (اختياري)"}
                              mode="basic"
                            />
                            {form.thumb ? <img src={form.thumb} alt="" style={{ maxWidth: 160, borderRadius: 8 }} /> : null}
                          </>
                        ) : null}
                        <FileUpload
                          name={`item-${section.id}`}
                          customUpload
                          uploadHandler={(e) => this.uploadItemFile(section.id, e)}
                          auto
                          disabled={!!form.uploading}
                          multiple={false}
                          accept={
                            form.itemType === "ARTICLE" ? "video/*" :
                            form.itemType === "VIDEO" ? "video/*" :
                            form.itemType === "AUDIO" ? "audio/*" :
                            form.itemType === "IMAGE" ? "image/*" :
                            form.itemType === "MODEL_3D" ? ".3dm" :
                            ".doc,.docx,.pdf,.ppt,.pptx"
                          }
                          maxFileSize={10737418240}
                          chooseLabel={form.uploading ? "جاري الرفع..." : form.contentUrl ? "إستبدال الملف" : form.itemType === "ARTICLE" ? "رفع فيديو (اختياري)" : "رفع الملف"}
                          uploadLabel="رفع"
                          cancelLabel="إلغاء"
                          mode="basic"
                        />
                        {form.uploading ? (
                          <div className="upload-progress-row">
                            <ProgressBar value={form.uploadProgress || 0} style={{ flex: 1, height: "10px" }} />
                            <span>{form.uploadProgress || 0}%</span>
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-danger p-button-sm"
                              title="إلغاء الرفع"
                              onClick={() => this.cancelItemUpload(section.id)}
                            />
                          </div>
                        ) : null}
                        {!form.uploading && form.contentUrl ? (
                          <div className="uploaded-file-row">
                            <i className="pi pi-check-circle" style={{ color: "green" }} />
                            <span>{form.fileName || "تم رفع الملف"}</span>
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-danger"
                              onClick={() => this.removeUploadedItemFile(section.id)}
                            />
                          </div>
                        ) : null}
                        <label>
                          <Checkbox
                            checked={!!form.isFreePreview}
                            onChange={(e) => this.updateItemForm(section.id, { isFreePreview: e.checked })}
                          />
                          {" "}معاينة مجانية
                        </label>
                        <label>
                          <Checkbox
                            checked={!!form.downloadAllowed}
                            onChange={(e) => this.updateItemForm(section.id, { downloadAllowed: e.checked })}
                          />
                          {" "}السماح بالتحميل
                        </label>
                        <Button label="إضافة العنصر" icon="pi pi-plus" onClick={() => this.addItem(section)} />
                      </div>

                      <div className="bulk-upload-form">
                        <FileUpload
                          name={`bulk-${section.id}`}
                          customUpload
                          uploadHandler={(e) => this.uploadBulkFiles(section, e)}
                          auto
                          multiple
                          accept="video/*,audio/*,image/*,.pdf,.doc,.docx,.ppt,.pptx,.3dm"
                          maxFileSize={10737418240}
                          chooseLabel="رفع عدة ملفات دفعة واحدة"
                          uploadLabel="رفع"
                          cancelLabel="إلغاء"
                          mode="basic"
                        />
                        <p className="bulk-upload-hint">
                          كل ملف هيتحول لعنصر مستقل بعنوان اسم الملف، ونوعه هيتحدد تلقائيًا حسب امتداده (فيديو/صوت/صورة/مستند/نموذج 3D).
                        </p>
                        {Object.entries(bulkUploads[section.id] || {}).map(([uploadId, info]) => (
                          <div className="upload-progress-row bulk-upload-progress-row" key={uploadId}>
                            <span className="bulk-upload-filename">{info.fileName}</span>
                            <ProgressBar value={info.progress} style={{ flex: 1, height: "10px" }} />
                            <span>{info.progress}%</span>
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-text p-button-danger p-button-sm"
                              title="إلغاء الرفع"
                              onClick={() => this.cancelBulkUpload(section.id, uploadId)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="add-item-form">
                  <InputText
                    placeholder="عنوان القسم الجديد"
                    value={this.state.newSectionTitle}
                    onChange={(e) => this.setState({ newSectionTitle: e.target.value })}
                  />
                  <Button label="إضافة قسم" icon="pi pi-plus" onClick={this.addSection} />
                </div>
              </Card>
            ) : (
              /* Sections live under /courses/{id}/sections, so there is
                 nothing to attach one to until the course exists. Rather
                 than hide the step and leave the teacher wondering where it
                 went, it is shown, named, and says what it waits for. */
              <Card className={view !== "all" ? "ui-step-hidden" : undefined}>
                <section className="ui-sec">
                  <h3>
                    <span className="n">٢</span>
                    <span className="t">
                      محتوى الدورة
                      <small>الأقسام والدروس اللي جوّه الدورة</small>
                    </span>
                  </h3>
                  <p className="ui-waiting">
                    احفظ بيانات الدورة في الخطوة الأولى، وهتقدر تضيف أقسامها ومحتواها هنا على طول.
                  </p>
                </section>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default CourseBuilder;
